import React from 'react'
import styled, { keyframes } from 'styled-components'
import Hero from '../components/Hero'
import { SliderDataEleven } from '../data/SliderData'
import image  from '../images/historic.jpg'
import image2 from '../images/shopping.jpg'
import image3 from '../images/golfing.jpg'
import image4 from '../images/spa.jpg'
import image5 from '../images/frontyard.png'
import image6 from '../images/festivals.jpg'

/* ─── Animations ─────────────────────────────────────────────── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ─── Page ───────────────────────────────────────────────────── */
const PageWrapper = styled.div`
  background: #f0e9e0;
  color: #2c1f14;
`

/* ─── Intro Section ──────────────────────────────────────────── */
const IntroSection = styled.section`
  padding: 110px 24px 90px;
  text-align: center;
  background: #f0e9e0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 50%; transform: translateX(-50%);
    width: 1px;
    height: 70px;
    background: linear-gradient(180deg, transparent, #b07d3a);
  }
`

const Eyebrow = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: #b07d3a;
  margin-bottom: 18px;
  animation: ${fadeUp} 0.8s ease both;
`

const PageHeading = styled.h1`
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 300;
  color: #2c1f14;
  margin: 0 auto 20px;
  line-height: 1.1;
  letter-spacing: -0.01em;
  animation: ${fadeUp} 0.85s 0.05s ease both;
`

const OrnamentDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 0 auto 36px;
  max-width: 220px;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, #b07d3a);
  }
  &::after {
    background: linear-gradient(90deg, #b07d3a, transparent);
  }
`

const OrnamentDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #b07d3a;
  flex-shrink: 0;
`

const IntroBody = styled.p`
  font-size: clamp(1.05rem, 1.5vw, 1.25rem);
  line-height: 1.9;
  color: #7a5c44;
  max-width: 720px;
  margin: 0 auto;
  font-style: italic;
  animation: ${fadeUp} 0.9s 0.15s ease both;
`

/* ─── Cards Section ──────────────────────────────────────────── */
const CardsSection = styled.section`
  padding: 0 24px 120px;
  background: #f0e9e0;
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`

const Card = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: #2c1f14;
  border: 1px solid rgba(176, 125, 58, 0.18);
  border-radius: 3px;
  background: #e8ddd0;
  overflow: hidden;
  animation: ${fadeUp} 0.8s ease both;
  animation-delay: ${p => p.delay || '0s'};
  transition: box-shadow 0.4s, transform 0.4s, border-color 0.4s;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #b07d3a, transparent);
    opacity: 0;
    transition: opacity 0.35s;
    z-index: 2;
  }

  &:hover {
    box-shadow: 0 24px 60px rgba(44, 31, 20, 0.14);
    transform: translateY(-6px);
    border-color: rgba(176, 125, 58, 0.45);
    &::before { opacity: 1; }
    img { transform: scale(1.05); }
    .card-arrow { transform: translateX(5px); }
  }
`

const CardImageWrapper = styled.div`
  width: 100%;
  height: 240px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 45%, rgba(44,31,20,0.5) 100%);
    pointer-events: none;
  }
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
`

const CardBody = styled.div`
  padding: 28px 28px 32px;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const CardLabel = styled.span`
  font-size: 0.62rem;
  letter-spacing: 0.35em;
  text-transform: uppercase;
  color: #b07d3a;
  display: block;
  margin-bottom: 10px;
`

const CardTitle = styled.h3`
  font-size: clamp(1.2rem, 1.6vw, 1.5rem);
  font-weight: 400;
  color: #2c1f14;
  margin: 0 0 12px;
  line-height: 1.3;
`

const CardText = styled.p`
  font-size: 0.92rem;
  color: #7a5c44;
  line-height: 1.75;
  margin: 0 0 auto;
  padding-bottom: 24px;
`

const CardArrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #b07d3a;
  transition: transform 0.3s;
`

/* ─── Activities Data ────────────────────────────────────────── */
const activities = [
  {
    image: image5,
    label: "Culture & Arts",
    title: "Arts",
    text: "From Performing Arts to Visual Arts, the Yadkin Valley has it all.",
    href: "https://www.yadkinarts.org/",
  },
  {
    image: image2,
    label: "Retail & Local",
    title: "Shopping",
    text: "Explore local shopping in all the charming small shops around the Valley.",
    href: "https://www.yadkinvalleync.com/attractions/?categories=&subcategories=shopping&town=",
  },
  {
    image: image3,
    label: "Recreation",
    title: "Golf",
    text: "Home to some of the most beautiful golf courses in North Carolina.",
    href: "https://www.golflink.com/golf-courses/nc/yadkinville/",
  },
  {
    image: image4,
    label: "Wellness",
    title: "Spas & Relaxation",
    text: "Treat yourself to a full day of rest, renewal, and pampering.",
    href: "https://www.yelp.com/search?cflt=spas&find_loc=Yadkin+County%2C+NC",
  },
  {
    image: image,
    label: "History",
    title: "Historic Sites",
    text: "Discover the rich history of the region through landmarks within easy driving distance.",
    href: "https://www.goyadkinvalley.com/map-the-corridor/historical-and-cultural/",
  },
  {
    image: image6,
    label: "Events",
    title: "Festivals",
    text: "Every year the Valley comes alive with festivals, fairs, and community celebrations.",
    href: "https://www.yelp.com/search?cflt=festivals&find_loc=Yadkin+County%2C+NC",
  },
]

/* ─── Component ──────────────────────────────────────────────── */
const Thingstodoscreen = () => {
  return (
    <PageWrapper>
      <Hero slides={SliderDataEleven} />

      <IntroSection>
        <Eyebrow>Yadkin Valley, North Carolina</Eyebrow>
        <PageHeading>Things to Do</PageHeading>
        <OrnamentDivider><OrnamentDot /></OrnamentDivider>
        <IntroBody>
          There is something for everyone in the Yadkin Valley — whether you're
          drawn to the arts, the outdoors, a round of golf, or a quiet afternoon
          at the spa. Come explore everything this beautiful region has to offer.
        </IntroBody>
      </IntroSection>

      <CardsSection>
        <CardsGrid>
          {activities.map((activity, i) => (
            <Card
              key={i}
              href={activity.href}
              target="_blank"
              rel="noopener noreferrer"
              delay={`${0.08 * i}s`}
            >
              <CardImageWrapper>
                <CardImage src={activity.image} alt={activity.title} />
              </CardImageWrapper>
              <CardBody>
                <CardLabel>{activity.label}</CardLabel>
                <CardTitle>{activity.title}</CardTitle>
                <CardText>{activity.text}</CardText>
                <CardArrow className="card-arrow">Explore →</CardArrow>
              </CardBody>
            </Card>
          ))}
        </CardsGrid>
      </CardsSection>
    </PageWrapper>
  )
}

export default Thingstodoscreen